import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox';
import type { FilterConfig, FilterOption } from '@openx/types/chipFilters';
import {
  appendUniqueMultiValueFilter,
  parseMultiValueFilter,
  serializeMultiValueFilter,
} from '@openx/utils/lib/multiValueFilter';
import { type KeyboardEvent, type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { DealsMoreFiltersAutocompleteField } from './DealsMoreFiltersAutocompleteField';
import {
  findFilterOptionByValue,
  getMoreFiltersSelectableOptionSx,
  getOptionalFilterValue,
  getSelectedFilterOption,
  getVisibleFilterOptions,
} from './dealsMoreFiltersShared';

export interface DealsMoreFiltersRememberedSelectFieldProps {
  filterConfig: FilterConfig;
  draftValue: string;
  rememberedValues: string[];
  onDraftChange: (value: string) => void;
  onRememberValue: (value: string) => void;
  onClearRememberedValues: () => void;
  rememberedOptionTestIdPrefix?: string;
  dataTest?: string;
  inputValue?: string;
  onInputValueChange?: (value: string) => void;
}

export function DealsMoreFiltersRememberedSelectField({
  filterConfig,
  draftValue,
  rememberedValues,
  onDraftChange,
  onRememberValue,
  onClearRememberedValues,
  rememberedOptionTestIdPrefix = 'remembered-option',
  dataTest = 'deals-more-filters-section',
  inputValue,
  onInputValueChange,
}: DealsMoreFiltersRememberedSelectFieldProps) {
  const { options = [], onInputChange, clearOnBlur, inputType, multiple } = filterConfig;
  const [internalInputValue, setInternalInputValue] = useState('');

  const filteredEmptyOptions = useMemo(() => getVisibleFilterOptions(options), [options]);
  const selectedValues = useMemo(() => parseMultiValueFilter(draftValue), [draftValue]);
  const resolvedInputValue = inputValue ?? internalInputValue;
  const setResolvedInputValue = onInputValueChange ?? setInternalInputValue;
  const isTextInput = inputType === 'text';

  const rememberedOptions = useMemo(
    () => rememberedValues.map(value => findFilterOptionByValue(filterConfig, value) ?? { value }),
    [filterConfig, rememberedValues]
  );

  const currentOption = useMemo(
    () => (multiple || isTextInput ? null : getSelectedFilterOption(filterConfig, draftValue)),
    [draftValue, filterConfig, isTextInput, multiple]
  );

  const applySelectedValue = useCallback(
    (value: string) => {
      const normalizedValue = value.trim();

      if (!normalizedValue) {
        return;
      }

      const nextValue = multiple ? appendUniqueMultiValueFilter(draftValue, normalizedValue) : normalizedValue;

      onDraftChange(nextValue);
      onRememberValue(normalizedValue);
      setResolvedInputValue('');
      onInputChange?.('');
    },
    [draftValue, multiple, onDraftChange, onInputChange, onRememberValue, setResolvedInputValue]
  );

  const handleOnInputChange = useCallback(
    (event: SyntheticEvent, value: string) => {
      if (event?.type !== 'change') {
        return;
      }

      if (isTextInput || multiple) {
        setResolvedInputValue(value);
      }

      onInputChange?.(value);
    },
    [isTextInput, multiple, onInputChange, setResolvedInputValue]
  );

  const handleChange = useCallback(
    (_: SyntheticEvent, option: FilterOption | string | null) => {
      if (!option) {
        if (isTextInput) {
          setResolvedInputValue('');
          return;
        }

        onDraftChange('');
        return;
      }

      const selectedValue = typeof option === 'string' ? option : String(option.value ?? '');
      if (multiple && !isTextInput) {
        const nextValues = selectedValues.includes(selectedValue)
          ? selectedValues.filter(value => value !== selectedValue)
          : [...selectedValues, selectedValue];

        onDraftChange(serializeMultiValueFilter(nextValues));
        onRememberValue(selectedValue);
        setResolvedInputValue('');
        onInputChange?.('');
        return;
      }

      applySelectedValue(selectedValue);
    },
    [
      applySelectedValue,
      isTextInput,
      multiple,
      onDraftChange,
      onInputChange,
      onRememberValue,
      selectedValues,
      setResolvedInputValue,
    ]
  );

  const handleBlur = useCallback(() => {
    if (clearOnBlur && onInputChange) {
      onInputChange('');
    }

    if (clearOnBlur || !isTextInput) {
      setResolvedInputValue('');
    }
  }, [clearOnBlur, isTextInput, onInputChange, setResolvedInputValue]);

  const handleClearSelected = useCallback(() => {
    onDraftChange('');
    onClearRememberedValues();
    setResolvedInputValue('');
  }, [onClearRememberedValues, onDraftChange, setResolvedInputValue]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isTextInput || event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      applySelectedValue(resolvedInputValue);
    },
    [applySelectedValue, isTextInput, resolvedInputValue]
  );

  const toggleRememberedValue = useCallback(
    (value: string) => {
      if (multiple) {
        const nextValues = selectedValues.includes(value)
          ? selectedValues.filter(selectedValue => selectedValue !== value)
          : [...selectedValues, value];

        onDraftChange(serializeMultiValueFilter(nextValues));
        return;
      }

      if (draftValue === value) {
        onDraftChange('');
        return;
      }

      onDraftChange(value);
    },
    [draftValue, multiple, onDraftChange, selectedValues]
  );

  return (
    <DealsMoreFiltersAutocompleteField
      filterConfig={filterConfig}
      currentOption={currentOption}
      selectedValues={selectedValues}
      inputValue={isTextInput || multiple ? resolvedInputValue : undefined}
      options={filteredEmptyOptions}
      onChange={handleChange}
      onInputChange={handleOnInputChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onClearSelected={handleClearSelected}
      clearDisabled={selectedValues.length === 0 && !resolvedInputValue}
      freeSolo={isTextInput}
      dataTest={dataTest}
    >
      {rememberedOptions.length > 0 && (
        <Stack
          direction="column"
          sx={{
            gap: 1,
          }}
        >
          {rememberedOptions.map(option => {
            const optionValue = getOptionalFilterValue(option.value?.toString()) ?? '';
            const isChecked = multiple ? selectedValues.includes(optionValue) : draftValue === optionValue;

            return (
              <FormControlLabel
                key={optionValue}
                data-test={`${rememberedOptionTestIdPrefix}-${optionValue}`}
                data-testid={`${rememberedOptionTestIdPrefix}-${optionValue}`}
                control={
                  <Checkbox checked={isChecked} onChange={() => toggleRememberedValue(optionValue)} size="small" />
                }
                label={option.displayName || option.value?.toString() || ''}
                sx={getMoreFiltersSelectableOptionSx({
                  isChecked,
                  minHeight: 28,
                  labelStyles: {
                    flex: 1,
                    fontWeight: 'fontWeightBold',
                    lineHeight: 1.25,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  },
                })}
              />
            );
          })}
        </Stack>
      )}
    </DealsMoreFiltersAutocompleteField>
  );
}
